import type { FC } from 'react';
import React, { Fragment } from 'react';
import { useDrop } from 'react-dnd';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { Box, IconButton, Stack } from '@mui/material';

import { DRAG_ITEM_TYPES } from 'src/constants/dragItem';
import { trimShapingName } from 'src/components/workspace/helper/helper';
import { useWorkspaceContext } from 'src/components/workspace/WorkspaceContext';

import { styles } from './index.styles';

export const DATALINE_SHAPING_TEST_ID = 'dataline-shaping';
export const FIRST_SHAPING_WRAPPER_TEST_ID = 'first-shaping-wrapper';
export const SECOND_SHAPING_WRAPPER_TEST_ID = 'second-shaping-wrapper';
export const DELETE_SHAPING_BUTTON_WRAPPER_TEST_ID =
  'delete-shaping-button-wrapper';

export interface ShapingComponentProps {
  /** is the shaping a placeholder for an orgUnit? */
  isDummy?: boolean;
  isReadOnly: boolean;
  /** needed to replace the placeholder */
  dummyParentRenderId?: string;
  /** shapingRenderId: string */
  shapingRenderId: string;
  /** array of value names (e.g. ['France', 'Germany']) */
  shapingNames: string[];
  /** array of values that correspond to shapingNames (e.g. [1, 2]) */
  shapingValues?: string[] | number[];
  /** display name of the variable (e.g. 'Country' or 'OrgUnit') */
  variableName: string;
  /** internal name of the variable (e.g. 'country' or 'orgUnit') */
  variable: string;
}

export const ShapingComponent: FC<ShapingComponentProps> = ({
  isDummy = false,
  isReadOnly,
  dummyParentRenderId = '',
  shapingRenderId,
  shapingNames,
  shapingValues = [],
  variableName,
  variable,
}) => {
  const { addValueToExistingShaping, deleteByRenderId, replaceOrgUnitDummy } =
    useWorkspaceContext();
  const isOrgUnit = variable === DRAG_ITEM_TYPES.ORG_UNIT;

  const [{ isOver, canDrop }, shapingElement] = useDrop({
    accept: [DRAG_ITEM_TYPES.SHAPING, DRAG_ITEM_TYPES.ORG_UNIT],

    drop: (dragItem) =>
      isDummy
        ? replaceOrgUnitDummy(dragItem, dummyParentRenderId)
        : addValueToExistingShaping(dragItem, shapingRenderId),

    canDrop: (dragItem: any) =>
      (dragItem.variableId === variable &&
        !shapingNames.includes(dragItem.title) &&
        !shapingNames.includes(dragItem.orgUnitId) &&
        !isReadOnly) ||
      (isDummy && dragItem.orgUnitId),

    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
    }),
  });

  const renderTrimmedShapingNames = () =>
    shapingNames.map((shapingName) => trimShapingName(shapingName)).join(', ');

  const renderOrgIds = () =>
    shapingValues.map((shapingValue, index) => (
      <Fragment key={shapingValue}>
        {index > 0 && ', '}
        {shapingValue}
      </Fragment>
    ));

  const generateTitle = () =>
    isOrgUnit
      ? shapingNames
          .map((valueName, index) => `${valueName} ${shapingValues[index]}`)
          .join(', ')
      : `${variableName}: ${shapingNames.join(', ')}`;

  return (
    <Box
      sx={styles.shaping(canDrop, isDummy, isOver)}
      ref={shapingElement}
      title={generateTitle()}
      data-test-id={DATALINE_SHAPING_TEST_ID}
    >
      <Stack direction="column">
        <Box data-test-id={FIRST_SHAPING_WRAPPER_TEST_ID}>
          {isOrgUnit ? renderTrimmedShapingNames() : `${variableName}:`}
        </Box>

        <Box data-test-id={SECOND_SHAPING_WRAPPER_TEST_ID}>
          {isOrgUnit ? renderOrgIds() : renderTrimmedShapingNames()}
        </Box>
      </Stack>

      {!isDummy && (
        <Stack
          direction="column"
          data-test-id={DELETE_SHAPING_BUTTON_WRAPPER_TEST_ID}
          sx={styles.buttonWrapper(canDrop, isOver)}
        >
          <IconButton
            sx={styles.button}
            disabled={isReadOnly}
            onClick={() => !isReadOnly && deleteByRenderId(shapingRenderId)}
            disableRipple
          >
            <CloseRounded fontSize="small" />
          </IconButton>
        </Stack>
      )}
    </Box>
  );
};

export default ShapingComponent;
